import React from 'react';
import { useTranslation } from 'react-i18next';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { SitemapElementsQuery } from '../../graphql/graphql';
import { Typography } from '../ui/Typography';
import {
  SitemapEntryContainer,
  SitemapLayout,
  SitemapLink,
  SitemapPageBackground,
  SitemapPageContainer,
  SitemapSectionContainer,
  SitemapSectionEntryContainer,
  SitemapSectionTitle,
  SitemapTitle
} from './styled';
import { getFormattedSitemapElements } from '../../utils/helpers/getFormattedSitemapElements';
import { ErrorPage } from '../ui/ErrorPage';
import { BackButtonIconEnum } from '../ui/PageHeader/types';

const PageHeader = dynamic(
  () => import('../ui/PageHeader').then(mod => mod.PageHeader),
  { ssr: false }
);

export const getRowCount = (totalItems, columnCount) =>
  Math.ceil(totalItems / columnCount);

export const SiteMap: React.FC<{
  sitemapElements: SitemapElementsQuery['sitemapElements'];
  error?: boolean;
}> = ({ sitemapElements = [], error }) => {
  const { t } = useTranslation();
  const router = useRouter();

  const { sitemapEntry, sitemapSection } =
    getFormattedSitemapElements(sitemapElements);

  const headerText = t('pages.sitemap.title');
  const errorPath = 'pages.sitemap.error';
  const message = t(`${errorPath}.message`);
  const title = t(`${errorPath}.title`);
  const buttonName = t(`${errorPath}.button`);

  return (
    <SitemapPageContainer>
      <PageHeader
        id="sitemap_mobile_header"
        title={headerText}
        onClick={() => router.push('/')}
        icon={BackButtonIconEnum.InternalNavigationBackButton}
      />
      {error ? (
        <ErrorPage {...{ message, title, buttonName }} />
      ) : (
        <SitemapPageBackground>
          <SitemapTitle tag="h1">{headerText}</SitemapTitle>
          <SitemapLayout>
            <SitemapEntryContainer>
              {sitemapEntry.map(e => (
                <SitemapLink href={e.path} key={e.name}>
                  <Typography tag="h5_new">{e.name}</Typography>
                </SitemapLink>
              ))}
            </SitemapEntryContainer>
            {sitemapSection.map(e => {
              const mobCountOfRows = getRowCount(e.entries.length, 2);
              const deskCountOfRows = getRowCount(e.entries.length, 4);
              return (
                <SitemapSectionContainer key={e.name}>
                  <SitemapSectionTitle>{e.name}</SitemapSectionTitle>
                  <SitemapSectionEntryContainer
                    $mobCountOfRows={mobCountOfRows}
                    $tabletCountOfRows={deskCountOfRows}
                    $deskCountOfRows={deskCountOfRows}
                  >
                    {e.entries.map(i => (
                      <SitemapLink href={i.path} key={i.name} title={i.name}>
                        <Typography>{i.name}</Typography>
                      </SitemapLink>
                    ))}
                  </SitemapSectionEntryContainer>
                </SitemapSectionContainer>
              );
            })}
          </SitemapLayout>
        </SitemapPageBackground>
      )}
    </SitemapPageContainer>
  );
};
